import { useState } from 'react';
import { Search, HelpCircle, ChevronDown, MessageCircle, Phone, Mail } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Layout } from '../components/layout/Layout';
import { cn } from '../lib/utils';
import { Link } from 'react-router-dom';

const faqCategories = [
  { id: 'booking', label: 'Booking & Reservations', icon: '🗓️' },
  { id: 'payment', label: 'Payments & Pricing', icon: '💳' },
  { id: 'cancellation', label: 'Cancellations & Refunds', icon: '❌' },
  { id: 'travel', label: 'Travel & Documents', icon: '✈️' },
  { id: 'packages', label: 'Tour Packages', icon: '🌟' },
  { id: 'general', label: 'General Questions', icon: '❓' },
];

const faqs = {
  booking: [
    {
      question: 'How do I book a tour package?',
      answer: 'You can book a tour package through our website by selecting your desired package, choosing your travel dates, and filling out the enquiry form. Our travel consultant will contact you within 24 hours to confirm your booking and guide you through the payment process.'
    },
    {
      question: 'Can I customize my tour package?',
      answer: 'Yes! We offer fully customizable tour packages. Just let us know your preferences, budget, and travel dates, and our expert team will create a personalized itinerary just for you. Contact us through the enquiry form or WhatsApp for custom packages.'
    },
    {
      question: 'How far in advance should I book?',
      answer: 'We recommend booking at least 2-4 weeks in advance for domestic tours and 4-6 weeks for international tours. During peak seasons (October-March, school holidays), booking 2-3 months ahead is advisable to secure the best rates and availability.'
    },
    {
      question: 'Can I book for a group?',
      answer: 'Absolutely! We specialize in group bookings for families, corporate teams, and friends. Group bookings of 10 or more people are eligible for special discounts. Contact our group booking team for customized quotes.'
    },
  ],
  payment: [
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept multiple payment methods including credit/debit cards (Visa, MasterCard, American Express), net banking, UPI (Google Pay, PhonePe, Paytm), and bank transfers. International customers can pay via PayPal or wire transfer.'
    },
    {
      question: 'Is there an advance payment required?',
      answer: 'Yes, we require a 25% advance payment to confirm your booking. The remaining 50% is due 15 days before departure, and the final 25% can be paid on arrival. For last-minute bookings, full payment may be required upfront.'
    },
    {
      question: 'Are there any hidden charges?',
      answer: 'No, we believe in complete transparency. All costs are clearly mentioned in our package details. Any additional expenses like personal shopping, optional activities, or tips are clearly listed under "Exclusions" in each package.'
    },
    {
      question: 'Do you offer EMI options?',
      answer: 'Yes, we offer No-Cost EMI options on select credit cards for bookings above ₹30,000. EMI tenure of 3, 6, 9, and 12 months is available. Check with your bank for applicable terms and conditions.'
    },
  ],
  cancellation: [
    {
      question: 'What is your cancellation policy?',
      answer: 'Our cancellation policy varies based on the timing: More than 30 days before departure: 90% refund. 15-30 days: 70% refund. 7-15 days: 50% refund. Less than 7 days: No refund. Force majeure situations are handled case by case.'
    },
    {
      question: 'How long does it take to process a refund?',
      answer: 'Refunds are processed within 7-10 working days after cancellation approval. The amount will be credited to your original payment method. Bank processing time may add an additional 3-5 days.'
    },
    {
      question: 'Can I reschedule my tour instead of cancelling?',
      answer: 'Yes, you can reschedule your tour up to 15 days before departure without any charges (subject to availability). Rescheduling within 15 days may incur a nominal fee. Contact our support team to arrange this.'
    },
    {
      question: 'What happens if you cancel my tour?',
      answer: 'In the rare event that we need to cancel a tour (due to insufficient bookings, natural disasters, etc.), you will receive a full 100% refund or the option to transfer your booking to an alternate date at no extra cost.'
    },
  ],
  travel: [
    {
      question: 'What documents do I need for travel?',
      answer: 'For domestic travel: A valid government ID (Aadhaar, Passport, Driving License, or Voter ID). For international travel: Valid passport with at least 6 months validity, applicable visas, travel insurance, and vaccination certificates if required.'
    },
    {
      question: 'Do you assist with visa applications?',
      answer: 'Yes, we provide complete visa assistance for international tours including document preparation, application submission, and tracking. Visa fees are usually not included in package prices and are payable separately.'
    },
    {
      question: 'Is travel insurance included?',
      answer: 'Travel insurance is optional for domestic tours and highly recommended for international tours. We can arrange comprehensive travel insurance covering medical emergencies, trip cancellations, and baggage loss at competitive rates.'
    },
    {
      question: 'What should I pack for my tour?',
      answer: 'We provide a detailed packing list specific to your destination and activities after booking confirmation. Generally, pack comfortable walking shoes, weather-appropriate clothing, medications, chargers, and photocopies of important documents.'
    },
  ],
  packages: [
    {
      question: 'What is included in your tour packages?',
      answer: 'Our standard packages include accommodation, specified meals (breakfast usually included), transportation between destinations, sightseeing as per itinerary, and services of an English-speaking guide. Specific inclusions are listed on each package page.'
    },
    {
      question: 'What type of accommodation do you provide?',
      answer: 'We offer 3-star, 4-star, and 5-star accommodation options. Standard packages include 3/4-star hotels. Premium and luxury upgrades are available. All hotels are personally inspected and selected for comfort, cleanliness, and location.'
    },
    {
      question: 'Are meals included in the package?',
      answer: 'Most packages include daily breakfast at the hotel. Lunch and dinner inclusions vary by package and are clearly mentioned in the itinerary. We can accommodate special dietary requirements like vegetarian, vegan, or Jain meals with advance notice.'
    },
    {
      question: 'What type of vehicles do you use for transportation?',
      answer: 'We use well-maintained, air-conditioned vehicles appropriate for the group size: sedans for 2-3 passengers, SUVs or Tempo Travellers for 4-8 passengers, and coaches for larger groups. All vehicles are driven by experienced, licensed drivers.'
    },
  ],
  general: [
    {
      question: 'How can I contact customer support?',
      answer: 'You can reach us through multiple channels: Phone: +91 9181268900 (9 AM - 7 PM), WhatsApp: Same number for instant chat, Email: support@nyravtours.com, or use the contact form on our website. We typically respond within 2-4 hours.'
    },
    {
      question: 'Do you offer corporate travel services?',
      answer: 'Yes, we have a dedicated corporate travel division handling team outings, conference travel, incentive trips, and business travel. We offer customized packages, corporate billing, and dedicated account managers for regular clients.'
    },
    {
      question: 'Are your tour guides certified?',
      answer: 'All our tour guides are professionally trained and certified. They have extensive knowledge of destinations, speak multiple languages, and are trained in first aid and emergency procedures. Local guides are also arranged for specialized experiences.'
    },
    {
      question: 'How do I leave a review or feedback?',
      answer: 'We value your feedback! You can leave a review on our website, Google, or social media pages. For direct feedback, email us at feedback@nyravtours.com. All reviews help us improve our services.'
    },
  ],
};

export default function FAQPage() {
  const [activeCategory, setActiveCategory] = useState('booking');
  const [searchQuery, setSearchQuery] = useState('');
  const [openItems, setOpenItems] = useState([]);

  // Get all FAQs for search
  const allFaqs = Object.entries(faqs).flatMap(([category, items]) =>
    items.map(item => ({ ...item, category }))
  );

  // Filter FAQs based on search
  const filteredFaqs = searchQuery
    ? allFaqs.filter(
        faq =>
          faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : faqs[activeCategory];

  const currentCategory = faqCategories.find(c => c.id === activeCategory);

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-[#004E64] to-[#006B87] py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-48 h-48 bg-[#FF6B35] rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <span className="inline-block text-[#FF6B35] text-sm font-semibold tracking-wider uppercase mb-4">
            ❓ Help Center
          </span>
          <h1 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Frequently Asked Questions
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
            Find answers to common questions about our tours, bookings, payments, and more.
          </p>

          {/* Search Bar */}
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search your question..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-14 rounded-full bg-white text-[#1A1A1A]"
            />
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Category Sidebar */}
            {!searchQuery && (
              <div className="lg:col-span-1">
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 sticky top-24">
                  <h3 className="font-semibold text-[#1A1A1A] mb-4">Categories</h3>
                  <div className="space-y-2">
                    {faqCategories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => setActiveCategory(category.id)}
                        className={cn(
                          'w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all',
                          activeCategory === category.id
                            ? 'bg-[#FF6B35] text-white'
                            : 'bg-gray-50 text-[#4A4A4A] hover:bg-gray-100'
                        )}
                      >
                        <span className="text-lg">{category.icon}</span>
                        <span className="text-sm font-medium">{category.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* FAQ Accordion */}
            <div className={cn('space-y-4', searchQuery ? 'lg:col-span-4' : 'lg:col-span-3')}>
              {!searchQuery && (
                <div className="mb-6">
                  <h2 
                    className="text-2xl font-bold text-[#1A1A1A] flex items-center gap-3"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    <span className="text-3xl">{currentCategory?.icon}</span>
                    {currentCategory?.label}
                  </h2>
                  <p className="text-[#4A4A4A] mt-2">
                    {filteredFaqs.length} questions in this category
                  </p>
                </div>
              )}

              {searchQuery && (
                <div className="mb-6">
                  <p className="text-[#4A4A4A]">
                    Found <span className="font-semibold text-[#1A1A1A]">{filteredFaqs.length}</span> results for &quot;{searchQuery}&quot;
                  </p>
                </div>
              )}

              {filteredFaqs.length > 0 ? (
                <Accordion type="multiple" value={openItems} onValueChange={setOpenItems}>
                  {filteredFaqs.map((faq, index) => (
                    <AccordionItem
                      key={index}
                      value={`faq-${index}`}
                      className="bg-white rounded-2xl border border-gray-100 shadow-sm mb-4 overflow-hidden"
                    >
                      <AccordionTrigger className="hover:no-underline px-6 py-5">
                        <div className="flex items-start gap-4 text-left">
                          <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                            <HelpCircle className="w-5 h-5 text-[#FF6B35]" />
                          </div>
                          <span className="font-semibold text-[#1A1A1A] text-lg">
                            {faq.question}
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-6 pb-6">
                        <div className="ml-14 text-[#4A4A4A] leading-relaxed">
                          {faq.answer}
                        </div>
                        {searchQuery && faq.category && (
                          <div className="ml-14 mt-4">
                            <span className="text-xs text-[#4A4A4A] bg-gray-100 px-3 py-1 rounded-full">
                              {faqCategories.find(c => c.id === faq.category)?.label}
                            </span>
                          </div>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                <div className="text-center py-12 bg-white rounded-2xl">
                  <HelpCircle className="w-16 h-16 text-[#4A4A4A] mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-[#1A1A1A] mb-2">No results found</h3>
                  <p className="text-[#4A4A4A] mb-4">Try a different search term or browse our categories.</p>
                  <Button onClick={() => setSearchQuery('')} variant="outline" className="rounded-full">
                    Clear Search
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Still Need Help */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 
            className="text-2xl md:text-3xl font-bold text-[#1A1A1A] mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Still Have Questions?
          </h2>
          <p className="text-[#4A4A4A] mb-8">
            Can&apos;t find the answer you&apos;re looking for? Our support team is here to help!
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link
              to="/contact"
              className="bg-[#FDFBF7] hover:bg-[#FF6B35]/10 rounded-2xl p-6 transition-colors group"
            >
              <Mail className="w-10 h-10 text-[#FF6B35] mx-auto mb-4" />
              <h3 className="font-semibold text-[#1A1A1A] mb-2">Email Us</h3>
              <p className="text-sm text-[#4A4A4A]">support@nyravtours.com</p>
            </Link>

            <a
              href="tel:+919181268900"
              className="bg-[#FDFBF7] hover:bg-[#FF6B35]/10 rounded-2xl p-6 transition-colors group"
            >
              <Phone className="w-10 h-10 text-[#FF6B35] mx-auto mb-4" />
              <h3 className="font-semibold text-[#1A1A1A] mb-2">Call Us</h3>
              <p className="text-sm text-[#4A4A4A]">+91 9181268900</p>
            </a>

            <a
              href="https://wa.me/919181268900"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#FDFBF7] hover:bg-[#25D366]/10 rounded-2xl p-6 transition-colors group"
            >
              <MessageCircle className="w-10 h-10 text-[#25D366] mx-auto mb-4" />
              <h3 className="font-semibold text-[#1A1A1A] mb-2">WhatsApp</h3>
              <p className="text-sm text-[#4A4A4A]">Quick Chat Support</p>
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
